import { useEffect, useState } from "react";
import { CheckCircle2, ShieldCheck, Undo2, LineChart, Gauge, Sparkles, Plug, ArrowRight, Lock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PublicLayout } from "@/components/PublicLayout";
import { ProductMockup } from "@/components/landing/ProductMockup";
import { Reveal } from "@/components/landing/Reveal";
import { HeroBackdrop } from "@/components/landing/HeroBackdrop";
import { Showcase } from "@/components/landing/Showcase";
import { AboutAetherTags } from "@/components/landing/AboutAetherTags";
import { Heading, NextStep, PainPoints, BeforeAfterSample, FreeToolsStrip, ResearchCard, Comparison, HomeFaq } from "@/components/landing/HomeSections";
import { FoundingBenefits, UspChips, StickyCta, scrollToWaitlist } from "@/components/landing/CtaBits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  [Plug, "01", "Connect", "Real shop data", "A Health Score for every listing, built from your own shop's numbers. Every stat says where it came from."],
  [ShieldCheck, "02", "Fix safely", "Safe changes", "AI fixes shown old-vs-new with a reason for each change. Bestsellers stay locked. Nothing changes until you click approve."],
  [Undo2, "03", "See proof", "Proven results", "Automatic backup, version history and one-click undo. Then compare edited listings with the ones you left alone."],
];

const FEATURES = [
  [Gauge, "#5EE6D0", "Health Score", "Every listing scored 0–100 from title, tags, attributes, description, photos and views trend, with a \"Fix these first\" list."],
  [Sparkles, "#8B7CFF", "AI fixes with reasons", "New title, 13 tags, attributes and description from Claude Opus 5.5, old vs new, with a reason for every change. Checked against Etsy's rules (13 tags of up to 20 characters, titles up to 140) before any push."],
  [ShieldCheck, "#E9C98B", "Bestseller Shield & one-click undo", "Listings with a sale in the last 60 days stay locked. Every push saves a backup to version history; Undo restores it in one click."],
  [LineChart, "#5EE6D0", "Proof view", "Edited vs untouched listings over 7, 14 and 30 days. Every number is labeled with its source."],
];

const field = "h-12 rounded-2xl border-white/10 bg-white/[0.04] px-4 text-[15px] placeholder:text-muted-foreground/70 focus-visible:ring-1 focus-visible:ring-[#E9C98B]/70 focus-visible:ring-offset-0";

const WaitlistForm = () => {
  const [form, setForm] = useState({ email: "", shop_url: "", listing_count: "", shop_type: "pod" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const { data } = await api.post("/waitlist", { ...form, listing_count: form.listing_count ? Number(form.listing_count) : null });
      setStatus({ ok: true, msg: data.message });
    } catch (err) {
      setStatus({ ok: false, msg: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  if (status?.ok) {
    return (
      <div id="waitlist" data-testid="waitlist-success" className="glass hairline mx-auto mt-10 max-w-2xl rounded-[28px] border p-8 text-center fade-up">
        <CheckCircle2 className="mx-auto h-9 w-9 text-[#5EE6D0] drop-shadow-[0_0_12px_rgba(94,230,208,0.7)]" />
        <p data-testid="waitlist-success-message" className="mt-4 font-display text-3xl sm:text-4xl">{status.msg}</p>
        <p className="mt-2 text-sm text-muted-foreground">We sent a confirmation to {form.email}. The founder invites beta shops personally.</p>
      </div>
    );
  }

  return (
    <form id="waitlist" onSubmit={submit} noValidate data-testid="waitlist-form" className="glass hairline-gold mx-auto mt-10 max-w-2xl rounded-[28px] border p-2.5 sm:p-3 text-left fade-up" style={{ animationDelay: "380ms" }}>
      <div className="flex flex-col gap-2">
        <Label htmlFor="wl-email" className="sr-only">Email</Label>
        <Input id="wl-email" data-testid="waitlist-email-input" value={form.email} onChange={set("email")} placeholder="you@yourshop.com" className={`${field} h-14 text-base`} />
      </div>
      <div className="mt-2">
        <Label htmlFor="wl-shop-url" className="sr-only">Your Etsy shop link</Label>
        <Input id="wl-shop-url" data-testid="waitlist-shop-url-input" value={form.shop_url} onChange={set("shop_url")} required
          placeholder="Your shop link: etsy.com/shop/YourShop" className={field} />
        <p data-testid="waitlist-shop-url-hint" className="px-3 pt-1.5 text-xs text-muted-foreground">So we can verify your shop size — we only look at your public shop page.</p>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <div>
          <Label htmlFor="wl-count" className="sr-only">Approx. listings</Label>
          <Input id="wl-count" type="number" min="0" data-testid="waitlist-listing-count-input" value={form.listing_count} onChange={set("listing_count")} placeholder="Approx. listings" className={field} />
        </div>
        <div>
          <Label className="sr-only">Shop type</Label>
          <Select value={form.shop_type} onValueChange={(v) => setForm({ ...form, shop_type: v })}>
            <SelectTrigger data-testid="waitlist-shop-type-select" className={field}><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["pod", "POD"], ["vintage", "Vintage"], ["handmade", "Handmade"], ["other", "Other"]].map(([v, l]) => (
                <SelectItem key={v} value={v} data-testid={`waitlist-shop-type-${v}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={busy} data-testid="waitlist-submit-button" className="btn-aurora col-span-2 h-14 rounded-2xl px-7 text-[15px] sm:col-span-1 sm:h-auto">
          {busy ? "Joining…" : "Join the waitlist"}<ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
      {status && !status.ok && (
        <p data-testid="waitlist-error-message" className="px-3 pt-3 text-sm text-rose-300">{status.msg}</p>
      )}
      <p data-testid="waitlist-subline" className="px-3 pb-1 pt-3 text-xs text-muted-foreground">Join the founding-member list. Free beta for 5 big shops. No card, no commitment.</p>
    </form>
  );
};

const Hero = () => (
    <section data-testid="hero" className="relative isolate overflow-hidden bg-[#07080F] pb-12 pt-28 sm:pb-14 sm:pt-28">
      <HeroBackdrop />
      <div className="relative mx-auto flex max-w-7xl flex-col items-center px-5 text-center sm:px-8">
        <div data-testid="founding-pill" className="fade-up inline-flex items-center gap-2 rounded-full border border-[#E9C98B]/35 bg-[#E9C98B]/[0.07] px-4 py-1.5 text-[12px] tracking-wide text-[#F3DDAE] backdrop-blur-md">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E9C98B] shadow-[0_0_10px_#E9C98B]" />
          Founding Member · Private Beta · 5 spots
        </div>
        <h1 data-testid="hero-h1" className="fade-up mt-6 sm:mt-8" style={{ animationDelay: "120ms" }}>
          <span data-testid="hero-seo-h1" className="block font-['Geist',ui-sans-serif,sans-serif] text-[12px] sm:text-sm font-medium uppercase tracking-[0.24em] text-[#F3DDAE]">
            The Etsy SEO tool for big catalogs
          </span>
          <span data-testid="waitlist-headline" className="mx-auto mt-4 block text-[2.6rem] min-[400px]:text-[3.05rem] leading-[0.98] sm:mt-5 sm:text-7xl lg:text-[7rem] lg:leading-[0.92] tracking-[-0.035em] [text-wrap:balance]">
            <span className="whitespace-nowrap">Real shop data.</span> <span className="whitespace-nowrap italic text-aurora pr-1">Safe changes.</span> <span className="whitespace-nowrap">Proven results.</span>
          </span>
        </h1>
        <p data-testid="hero-intro" className="fade-up order-2 mx-auto mt-8 max-w-2xl text-base sm:order-none sm:text-lg text-muted-foreground leading-relaxed" style={{ animationDelay: "240ms" }}>
          The safe growth system for big Etsy catalogs. AetherTags is the Etsy SEO tool for POD and vintage shops with hundreds of listings, and it turns
          Etsy listing optimization into a safe loop: see which listings to fix first, approve AI fixes in batches, push with backup and one-click undo,
          and compare edited listings with untouched ones in your own numbers.
        </p>
        <div className="order-1 w-full sm:order-none">
          <WaitlistForm />
          <UspChips testid="hero-usp-chips" className="mx-auto mt-5 max-w-2xl" />
          <FoundingBenefits testid="hero-founding-benefits" className="mt-4" />
        </div>
      </div>
      <ProductMockup />
    </section>
);

const HowItWorks = () => (
  <section data-testid="how-it-works" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-14">
    <div className="grid gap-10 lg:grid-cols-[1fr_1.05fr] lg:items-start lg:gap-14">
      <div>
        <Heading eyebrow="How it works · Connect, fix safely, see proof" className="max-w-xl">
          Etsy listing optimization, <span className="italic text-gold-grad">without the risk.</span>
        </Heading>
        <p data-testid="steps-copy" className="mt-4 text-[15px] sm:text-base text-muted-foreground leading-relaxed">
          Etsy listing optimization works best as a loop, not a one-off rewrite: connect your shop, review fixes in batches, then compare edited
          listings with untouched ones, so SEO for Etsy becomes something you can measure instead of guess.
        </p>
        <ol className="mt-7 space-y-3">
          {STEPS.map(([Icon, n, step, title, text], i) => (
            <Reveal as="li" key={title} delay={i * 100} data-testid={`pillar-${title.toLowerCase().replace(/ /g, "-")}`} className="glass hairline flex gap-4 rounded-2xl border px-5 py-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-white/10 bg-[#0E1020]"><Icon className="h-4 w-4 text-[#E9C98B]" /></span>
              <div>
                <p className="font-mono text-[11px] text-muted-foreground">{n} · {step}</p>
                <h3 className="mt-0.5 font-display text-xl sm:text-2xl leading-tight">{title}</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
      <div className="space-y-4 lg:pt-8">
        <BeforeAfterSample />
        <Reveal data-testid="safety-line" className="flex gap-3 rounded-2xl border border-[#E9C98B]/25 bg-[#E9C98B]/[0.05] p-5 text-[15px] leading-relaxed">
          <Lock className="mt-1 h-4 w-4 shrink-0 text-[#E9C98B]" />
          <p><strong className="font-medium text-[#F3DDAE]">Bulk edit Etsy listings, safely.</strong> <span className="text-muted-foreground">Bestseller Shield locks any listing with a sale in the last 60 days, nothing changes until you click approve, and every batch saves a backup that one click undoes.</span></p>
        </Reveal>
        <NextStep onClick={scrollToWaitlist} testid="how-next-step">Get your personal shop audit: join the founding list</NextStep>
      </div>
    </div>
  </section>
);

const Card = ({ testid, className = "", children }) => (
  <Reveal data-testid={testid} className={`glass hairline lift relative overflow-hidden rounded-2xl border p-5 ${className}`}>{children}</Reveal>
);

const WhatWorksToday = () => (
  <section data-testid="live-today" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-14">
    <Heading eyebrow={<span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">Inside AetherTags<span data-testid="live-in-beta" className="inline-flex items-center gap-2 text-emerald-200"><span className="live-dot h-2 w-2 rounded-full bg-emerald-300 shadow-[0_0_10px_#6ee7b7]" />Live in beta today</span></span>}
      sub="An Etsy title generator and Etsy tag generator built for catalogs too big to edit by hand, with every change checked against Etsy's rules before it goes live.">
      What the Etsy SEO tool does <span className="italic text-gold-grad">in the beta right now.</span>
    </Heading>
    <div className="mt-7 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      <Card testid="pod-vintage-card" className="!p-0 md:col-span-2 lg:col-span-1 lg:row-span-2">
        <img src="/images/still-life.webp" alt="Vintage and print-on-demand Etsy products ready for Etsy listing optimization: a vintage camera, ceramic vase, gold jewelry, an art print and a printed tee" loading="lazy" decoding="async" width="1264" height="848" className="aspect-[2/1] w-full object-cover lg:aspect-[16/9]" />
        <div className="p-6">
          <h3 className="font-display text-2xl leading-tight">Etsy SEO for print on demand <span className="italic text-aurora">and vintage shops</span></h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Etsy SEO for print on demand means many similar listings that each need distinct words; for vintage, every one-of-a-kind find needs its own. Every connected shop starts with an audit: a Health Score for every listing.</p>
        </div>
      </Card>
      {FEATURES.map(([Icon, color, title, text], i) => (
        <Card key={title} testid={`feature-card-${i}`}>
          <h3 className="flex items-center gap-2.5 font-display text-2xl leading-tight"><Icon className="h-4 w-4 shrink-0" style={{ color }} />{title}</h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
        </Card>
      ))}
      <Card testid="privacy-card">
        <h3 className="flex items-center gap-2.5 font-display text-2xl leading-tight"><Plug className="h-4 w-4 shrink-0 text-[#5EE6D0]" />Your shop, your approval</h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          An Etsy SEO tool that works through Etsy's official API: no scraping, no browser extension, and you sign in on Etsy's own page, so we
          never see your password. Nothing is pushed without your "Approve &amp; push" click, and your listing content is never used to train AI models.
        </p>
      </Card>
      <ResearchCard />
    </div>
  </section>
);

const MidCta = () => (
  <div data-testid="mid-cta" className="mx-auto max-w-7xl px-5 sm:px-8">
    <Reveal className="glass hairline-gold flex flex-col gap-5 rounded-[28px] border px-6 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-10">
      <div>
        <p className="font-display text-2xl sm:text-3xl leading-tight">Free beta for 5 big shops. <span className="italic text-aurora">No card, no commitment.</span></p>
        <FoundingBenefits testid="mid-founding-benefits" center={false} className="mt-3" />
      </div>
      <Button data-testid="cta-mid-join-waitlist-button" className="btn-aurora h-14 w-full shrink-0 rounded-2xl px-8 text-[15px] sm:w-auto" onClick={scrollToWaitlist}>
        Join the founding list<ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </Reveal>
  </div>
);

const FaqAbout = () => (
  <section data-testid="faq-about" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-14">
    <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
      <AboutAetherTags />
      <HomeFaq />
    </div>
  </section>
);

const ClosingCta = () => (
  <section data-testid="closing-cta" className="mx-auto max-w-7xl px-5 pb-8 sm:px-8">
    <Reveal className="hairline relative overflow-hidden rounded-[36px] px-6 py-12 text-center sm:px-12 sm:py-14">
      <img src="/images/glass.webp" alt="Gold and violet light through glass, behind the AetherTags founding-member invitation for big Etsy shops" loading="lazy" decoding="async" width="1264" height="848" className="absolute inset-0 h-full w-full object-cover opacity-60" />
      <div className="aurora-layer opacity-50" />
      <div className="absolute inset-0 bg-[#07080F]/40" />
      <div className="relative">
        <p className="eyebrow">Founding Member · Private Beta · 5 spots</p>
        <h2 className="mx-auto mt-4 max-w-3xl text-[2.15rem] sm:text-5xl leading-[1]">Something big for <span className="italic text-aurora">big catalogs.</span></h2>
        <p className="mx-auto mt-5 max-w-xl text-muted-foreground">The Etsy SEO tool for big catalogs is free during the beta, and founding members lock a lifetime founding price. The founder invites each shop personally.</p>
        <Button data-testid="cta-join-waitlist-button" className="btn-aurora mt-8 h-14 rounded-2xl px-8 text-[15px]" onClick={scrollToWaitlist}>
          Join the founding list<ArrowRight className="ml-1 h-4 w-4" />
        </Button>
        <FoundingBenefits testid="closing-founding-benefits" className="mt-6" />
      </div>
    </Reveal>
  </section>
);

export default function Landing() {
  useEffect(() => {
    if (window.location.hash === "#waitlist") setTimeout(scrollToWaitlist, 300);
  }, []);
  return (
    <PublicLayout overlay hideFounding>
      <Hero />
      <PainPoints />
      <div className="gold-divider" />
      <HowItWorks />
      <div className="gold-divider" />
      <WhatWorksToday />
      <MidCta />
      <FreeToolsStrip />
      <div className="gold-divider" />
      <Comparison />
      <div className="gold-divider" />
      <Showcase />
      <div className="gold-divider" />
      <FaqAbout />
      <ClosingCta />
      <StickyCta />
    </PublicLayout>
  );
}
